// ** React
import { useEffect, useState } from "react";

// ** Redux
import { useSelector, useDispatch } from "react-redux";
import { setCookieConsentBannerVisibility } from "store/modules/global-common.slice";

// ** Third Party
import { useTranslation } from "react-i18next";
import styled, { css } from "styled-components";

// Components
import Text from "components/typography/Text";
import { ButtonPrimary } from "components/layout/Button/ButtonPrimary";
import { ButtonSecondary } from "components/layout/Button/ButtonSecondary";
import Link from "components/layout/Link";
import Toggle from "components/layout/Toggle/Toggle";
import Block from "components/layout/Block";
import Heading from "components/typography/Heading";

// ** Hooks
import { useConsentService } from "hooks/useConsentService";
// ** Utils & Helpers
import formatMessage from "utils/formatMessage";

// ** Styles Import
import { breakpoints } from "styles/grid";

// ##############################################################

const ConsentBanner = styled.div`
	position: fixed;
	bottom: 0;
	left: 0;
	right: 0;
	background-color: var(--dz-sys-card-background-color);
	border-top: 1px solid var(--dz-sys-divider-color);
	padding: 1rem 0;
	z-index: 999;
	box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
	display: flex;
	justify-content: center;
`;

const ConsentBannerContent = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	gap: 0.75rem;
	padding-left: 1rem;
	padding-right: 1rem;

	@media (max-width: ${breakpoints.phone - 1}px) {
		width: 100%;
	}

	@media (min-width: ${breakpoints.tablet}px) {
		width: 599px;
	}

	@media (min-width: ${breakpoints.desktop}px) {
		padding-left: 1.25rem;
		width: 1049px;
	}

	@media (min-width: ${breakpoints.giant}px) {
		width: 1340px;
	}
`;

const ConsentBannerButtons = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: end;
	align-items: center;
	gap: 0.75rem;
	width: 100%;

	@media (min-width: ${breakpoints.desktop}px) {
		width: unset;
		flex-shrink: 0;
	}
`;

const CustomizationSection = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	gap: 0.25rem;
	padding: 1rem 0 0 0;
	border-top: 1px solid var(--dz-sys-divider-color);
	margin-top: 0.5rem;
`;

const ToggleWrapper = styled.div`
	display: grid;
	grid-template-columns: 1fr auto;
	align-items: center;
	gap: 1rem;
	width: auto;
	padding: 0.5rem 1rem;
	border-radius: ${({ theme: { borderRadius } }) => borderRadius.element};
	user-select: none;

	${({ disabled }) =>
		disabled
			? css`
					cursor: no-drop;
					& ${Text} {
						opacity: var(--dz-sys-button-disabled-opacity);
					}
				`
			: css`
					cursor: pointer;
				`}
	&:hover {
		background-color: ${({ theme: { colors } }) => colors.itemsHover};
	}
	@media (min-width: ${breakpoints.desktop}px) {
		min-width: 300px;
	}
`;

// ##############################################################

const CookieConsentBanner = () => {
	const { t } = useTranslation(["common", "landing"]);
	const dispatch = useDispatch();
	const showBanner = useSelector(
		(state) => state.globalCommon.cookieConsent.showBanner,
	);
	const showCustomization = useSelector(
		(state) => state.globalCommon.cookieConsent.showCustomization,
	);

	const {
		consentValue,
		CONSENT_TYPES,
		getStoredCustomSettings,
		grantFullConsent,
		grantNecessaryConsent,
		grantCustomConsent,
	} = useConsentService();

	const [customConsentSettings, setCustomConsentSettings] = useState({
		[CONSENT_TYPES.ANALYTICS_STORAGE]: true,
		[CONSENT_TYPES.AD_STORAGE]: true,
	});

	useEffect(() => {
		if (!consentValue) {
			// Brak zgody - pokaż banner, domyślnie wszystkie zaznaczone
			dispatch(
				setCookieConsentBannerVisibility({
					showBanner: true,
					showCustomization: false,
				}),
			);
			setCustomConsentSettings({
				[CONSENT_TYPES.ANALYTICS_STORAGE]: true,
				[CONSENT_TYPES.AD_STORAGE]: true,
				[CONSENT_TYPES.FUNCTIONALITY_STORAGE]: true,
				[CONSENT_TYPES.SECURITY_STORAGE]: true,
			});
		} else {
			// Zgoda istnieje - banner domyślnie ukryty
			dispatch(
				setCookieConsentBannerVisibility({
					showBanner: false,
					showCustomization: false,
				}),
			);

			// Ustaw stan przełączników na podstawie zapisanej zgody
			if (consentValue === "custom") {
				const storedSettings = getStoredCustomSettings();
				if (storedSettings) {
					setCustomConsentSettings(storedSettings);
				} else {
					// Fallback, jeśli szczegóły są niedostępne (powinno być obsługiwane przez initializeConsent)
					setCustomConsentSettings({
						[CONSENT_TYPES.ANALYTICS_STORAGE]: false,
						[CONSENT_TYPES.AD_STORAGE]: false,
						[CONSENT_TYPES.FUNCTIONALITY_STORAGE]: true,
						[CONSENT_TYPES.SECURITY_STORAGE]: true,
					});
				}
			} else if (consentValue === "all") {
				setCustomConsentSettings({
					[CONSENT_TYPES.ANALYTICS_STORAGE]: true,
					[CONSENT_TYPES.AD_STORAGE]: true,
					[CONSENT_TYPES.FUNCTIONALITY_STORAGE]: true,
					[CONSENT_TYPES.SECURITY_STORAGE]: true,
				});
			} else {
				// necessary
				setCustomConsentSettings({
					[CONSENT_TYPES.ANALYTICS_STORAGE]: false,
					[CONSENT_TYPES.AD_STORAGE]: false,
					[CONSENT_TYPES.FUNCTIONALITY_STORAGE]: true,
					[CONSENT_TYPES.SECURITY_STORAGE]: true,
				});
			}
		}
	}, []);

	const handleAcceptAll = () => {
		grantFullConsent();
		dispatch(
			setCookieConsentBannerVisibility({
				showBanner: false,
				showCustomization: false,
			}),
		);
	};

	const handleCustomize = () => {
		dispatch(
			setCookieConsentBannerVisibility({
				showBanner: true,
				showCustomization: true,
			}),
		);
	};

	const handleToggleChange = (consentType) => {
		setCustomConsentSettings((prevSettings) => ({
			...prevSettings,
			[consentType]: !prevSettings[consentType],
		}));
	};

	const handleSavePreferences = () => {
		// Sprawdź stan konfigurowalnych zgód
		const analyticsConsent =
			customConsentSettings[CONSENT_TYPES.ANALYTICS_STORAGE];
		const adConsent = customConsentSettings[CONSENT_TYPES.AD_STORAGE];

		// Warunek dla "all": wszystkie konfigurowalne muszą być true
		const isAllGranted = analyticsConsent && adConsent; // && personalizationConsent;

		// Warunek dla "necessary": wszystkie konfigurowalne muszą być false
		const isOnlyNecessaryGranted = !analyticsConsent && !adConsent; // && !personalizationConsent;

		if (isAllGranted) {
			grantFullConsent();
		} else if (isOnlyNecessaryGranted) {
			grantNecessaryConsent();
		} else {
			// W przeciwnym razie zapisz jako "custom"
			grantCustomConsent(customConsentSettings);
		}

		dispatch(
			setCookieConsentBannerVisibility({
				showBanner: false,
				showCustomization: false,
			}),
		);
	};

	if (!showBanner) {
		return null;
	}

	const handleCloseBanner = () => {
		dispatch(
			setCookieConsentBannerVisibility({
				showBanner: false,
				showCustomization: false,
			}),
		);
	};

	return (
		<ConsentBanner>
			<ConsentBannerContent>
				<Block>
					<Heading type="h3">
						{t("common:cookies.serviceMenuLink")}
					</Heading>
				</Block>
				<Text>
					{formatMessage(t("common:cookies.consentText"), {
						privacyPolicy: (
							<Link
								underline
								to="/privacy"
								key="common:cookies.privacyPolicyLink"
							>
								{t(`common:cookies.privacyPolicyLink`)}
							</Link>
						),
					})}
				</Text>

				{!showCustomization ? (
					<ConsentBannerButtons>
						<ButtonSecondary
							onClick={handleCustomize}
							label={t("common:cookies.customize")}
						/>
						<ButtonPrimary
							onClick={handleAcceptAll}
							label={t("common:cookies.acceptAll")}
						/>
					</ConsentBannerButtons>
				) : (
					<CustomizationSection>
						<Text bold>{t("common:cookies.consentHeader")}</Text>
						<ToggleWrapper disabled={true}>
							<Block>
								<Text>
									{t("common:cookies.necessaryStorage")}
								</Text>
							</Block>
							<Toggle toggled={true} disabled={true} />
						</ToggleWrapper>
						<ToggleWrapper
							onClick={() => {
								handleToggleChange(
									CONSENT_TYPES.ANALYTICS_STORAGE,
								);
							}}
						>
							<Text>{t("common:cookies.analyticsStorage")}</Text>
							<Toggle
								toggled={
									customConsentSettings[
										CONSENT_TYPES.ANALYTICS_STORAGE
									]
								}
							/>
						</ToggleWrapper>
						<ToggleWrapper
							onClick={() => {
								handleToggleChange(CONSENT_TYPES.AD_STORAGE);
							}}
						>
							<Text>{t("common:cookies.adStorage")}</Text>
							<Toggle
								toggled={
									customConsentSettings[
										CONSENT_TYPES.AD_STORAGE
									]
								}
							/>
						</ToggleWrapper>
						<ConsentBannerButtons>
							{consentValue && (
								<ButtonSecondary
									onClick={handleCloseBanner}
									label={t("common:cancel")}
								/>
							)}
							<ButtonPrimary
								onClick={handleSavePreferences}
								label={t("common:cookies.savePreferences")}
								fullWidthMobile={consentValue ? false : true}
							/>
						</ConsentBannerButtons>
					</CustomizationSection>
				)}
			</ConsentBannerContent>
		</ConsentBanner>
	);
};

export default CookieConsentBanner;
